import { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import discountService from '@/app/[locale]/_service/discount.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import { useQuery } from '@tanstack/react-query'
import { IDiscount } from '@/app/[locale]/_types'
import { INewDiscountParams } from '@/app/[locale]/_service/types'
import dayjs from 'dayjs'
import {
  resolveOrderSourceId,
  resolvePaymentTypeId,
} from '@/app/[locale]/_utils/aggregator-context'

export default function useProductDiscount(): {
  discounts: IDiscount[]
} {
  const [discounts, setDiscounts] = useState<IDiscount[]>([])

  const { auth, cart, common, checkout, session, points } = useSelector(
    (state: IRedux) => ({
      auth: state.auth,
      cart: state.cart,
      common: state.common,
      checkout: state.checkout,
      session: state.session,
      points: state.common?.points,
    }),
    shallowEqual
  )

  const user = auth.user
  const orderSourceId = resolveOrderSourceId(session, auth)
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  const cartState = cart
  const commonState = common
  const checkoutState = checkout
  const paymentTypeId = resolvePaymentTypeId(checkoutState, auth)
  const { order_type } = session

  const products = useMemo(() => {
    let acc = 0
    for (const product of cartState?.cart) {
      let productTotal = (product.price_with_discount || 0) * product.quantity
      if (product.order_modifiers?.length) {
        product.order_modifiers.forEach((modifier: any) => {
          productTotal +=
            +modifier.modifier_price *
            +(modifier.modifier_quantity * product.quantity)
        })
      }
      acc += productTotal
    }
    return { totalPrice: acc }
  }, [cartState?.cart])

  const activePoint = useMemo(
    () => points?.find((point) => point?.isActive),
    [points]
  )

  const discountParams: INewDiscountParams = useMemo(
    () => ({
      delivery_price: 0,
      with_cashback: false,
      for_order_amount: products.totalPrice,
      usable_with_others: undefined,
      discount_for: 'product,order',
      client_id: user?.id || '',
      order_type_id: order_type?.id,
      branch_ids: commonState?.branch?.id,
      order_source_id: orderSourceId,
      payment_type_id: paymentTypeId,
      lat: activePoint?.location?.[0],
      long: activePoint?.location?.[1],
      future_time: checkoutState?.future_time
        ? dayjs(checkoutState.future_time).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    }),
    [
      products.totalPrice,
      user?.id,
      order_type?.id,
      commonState?.branch?.id,
      orderSourceId,
      paymentTypeId,
      checkoutState?.future_time,
      activePoint?.location,
    ]
  )

  // Get order discount
  const { data: allDiscounts, isSuccess } = useQuery({
    queryKey: ['ALL_DISCOUNTS', discountParams, dynamicLanguageEnabled],
    queryFn: () =>
      discountService.all(discountParams, undefined, dynamicLanguageEnabled),
    enabled: Boolean(order_type?.id) && isShipperResolved,
    retry: false,
  })

  useEffect(() => {
    if (isSuccess) {
      const filtered = allDiscounts?.data?.discounts?.reduce(
        (acc: { product: IDiscount[]; order: IDiscount[] }, cur: IDiscount) => {
          if (cur.discount_for === 'product') {
            acc.product.push(cur)
          }

          return acc
        },
        { product: [], order: [] }
      )

      setDiscounts(filtered?.product ?? [])
    }
  }, [allDiscounts, isSuccess])

  return { discounts }
}
